/**
 * InstantHitWeaponComponent - fires instant hit rays with configurable damage and effects.
 * Performs raycast-based hit detection for weapons like rifles and pistols.
 */

import * as THREE from 'three';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { Delegate } from '../../utils/Delegate.js';
import { property } from '../../utils/serialization/decorator.js';
import { CharacterStatsComponent } from '../CharacterStatsComponent.js';

import { BaseWeaponComponent } from './BaseWeaponComponent.js';

import type { BaseWeaponOptions } from './BaseWeaponComponent.js';
import type { Actor } from '../../actors/Actor.js';
import type { HitTestResult } from '../../physics/PhysicsEngine.js';

export interface InstantHitWeaponOptions extends BaseWeaponOptions {
  /**
   * Amount of damage dealt per hit.
   */
  damage?: number;

  /**
   * Optional spread angle in radians for accuracy simulation.
   * 0 means perfect accuracy.
   */
  spread?: number;
}

export interface HitInfo {
  /**
   * The actor that was hit.
   */
  hitActor: Actor | null;
  /**
   * The exact world position where the hit occurred.
   */
  hitLocation: THREE.Vector3;
  /**
   * The surface normal at the hit point.
   */
  hitNormal: THREE.Vector3;
  /**
   * The distance from the weapon to the hit point.
   */
  distance: number;
  /**
   * The damage dealt to the target.
   */
  damageDealt: number;
}

export type HitDelegate = Delegate<[HitInfo], void>;

@EngineClass('InstantHitWeaponComponent')
export class InstantHitWeaponComponent extends BaseWeaponComponent {


  @property({ type: 'number', min: 0, step: 1, decimals: 0 })
  protected damage: number = 25;

  @property({ type: 'number', min: 0, max: 1, step: 0.01, decimals: 3 })
  protected spread: number = 0;

  public readonly onHit: HitDelegate = new Delegate<[HitInfo], void>();

  public override initialize(options?: InstantHitWeaponOptions): void {
    super.initialize(options);
    this.damage = options?.damage ?? this.damage;
    this.spread = options?.spread ?? this.spread;
  }

  protected doFire(): void {
    // Guard against firing without ammo
    if (!this.canFire()) {
      return;
    }

    const { position, direction } = this.getFirePositionAndDirection();
    let fireDirection = direction.clone();

    // Apply target direction if available
    const targetDirection = this.getTargetDirection();
    if (targetDirection) {
      fireDirection = targetDirection;
    }

    // Apply spread for accuracy simulation
    if (this.spread > 0) {
      fireDirection = this.applySpread(fireDirection, this.spread);
    }

    // Perform the instant hit trace
    this.performInstantHit(position, fireDirection);

    // Handle weapon effects and ammo
    this.consumeAmmo();
    this.playFireSound();
    this.applyRecoilEffects();
  }

  /**
   * Applies random spread to the fire direction for accuracy simulation.
   * @param direction The base fire direction
   * @param spreadAngle The maximum spread angle in radians
   * @returns The direction with applied spread
   */
  protected applySpread(direction: THREE.Vector3, spreadAngle: number): THREE.Vector3 {
    // Create a random offset within a cone
    const phi = Math.random() * Math.PI * 2; // Random angle around the direction
    const theta = Math.random() * spreadAngle; // Random angle from center up to max spread

    // Convert spherical coordinates to cartesian offset
    const x = Math.sin(theta) * Math.cos(phi);
    const y = Math.sin(theta) * Math.sin(phi);
    const z = Math.cos(theta);

    // Create a coordinate system based on the fire direction
    const up = new THREE.Vector3(0, 1, 0);
    const right = new THREE.Vector3().crossVectors(direction, up).normalize();
    const forward = direction.clone().normalize();
    up.crossVectors(right, forward).normalize();

    // Apply the spread offset
    const spreadDirection = new THREE.Vector3()
      .addScaledVector(right, x)
      .addScaledVector(up, y)
      .addScaledVector(forward, z)
      .normalize();

    return spreadDirection;
  }

  /**
   * Performs the instant hit raycast and handles hit results.
   * @param origin The starting position of the ray
   * @param direction The direction of the ray
   */
  protected performInstantHit(origin: THREE.Vector3, direction: THREE.Vector3): void {
    const world = this.getWorld();
    const actor = this.getActor();
    if (!world || !actor) {
      return;
    }

    // Perform raycast hit test
    const hitResults = this.getPhysicsEngine()?.performHitTest({
      origin: origin,
      direction: direction,
      maxDistance: this.range,
      stopOnFirstHit: true,
      ignoredActors: [actor],
    });

    for (const hitResult of hitResults ?? []) {
      this.processHit(hitResult, origin);
    }
  }

  /**
   * Processes a single hit result and applies damage/effects.
   * @param hitResult The hit test result
   * @param fireOrigin The original fire position for distance calculation
   */
  protected processHit(hitResult: HitTestResult, fireOrigin: THREE.Vector3): void {
    const hitInfo: HitInfo = {
      hitActor: hitResult.hitActor ?? null,
      hitLocation: hitResult.hitLocation.clone(),
      hitNormal: hitResult.hitNormal.clone(),
      distance: fireOrigin.distanceTo(hitResult.hitLocation),
      damageDealt: this.damage,
    };

    // Apply damage to the hit actor if it has a health component
    if (hitResult.hitActor && this.damage > 0) {
      this.applyDamage(hitResult.hitActor, this.damage, hitResult);
    }

    // Show hit effects if enabled
    this.playHitEffect(hitInfo);

    // Emit hit event
    this.onHit.invoke(hitInfo);
  }

  /**
   * Applies damage to the target actor.
   * Override this method to customize damage application logic.
   * @param target The actor to damage
   * @param damage The amount of damage to apply
   * @param hitInfo The hit test result
   */
  protected applyDamage(target: Actor, damage: number, hitInfo: HitTestResult): void {
    const statsComponent = target.getComponent(CharacterStatsComponent);
    statsComponent?.takeDamage(damage, {
      hitLocation: hitInfo.hitLocation.clone(),
      hitNormal: hitInfo.hitNormal.clone(),
    });
  }

  /**
   * Plays visual hit effects at the hit location.
   * Override this method to customize hit effects.
   * @param hitInfo Information about the hit
   */
  protected playHitEffect(hitInfo: HitInfo): void {
    const world = this.getWorld();
    if (!world) return;

    // Spawn impact spark effect at hit location
    void world.globalParticleManager.spawnVFX(
      'hitSparks',
      {
        nbParticles: 100,
        intensity: 5,
        renderMode: 'billboard',
        appearance: 'circular',
        fadeAlpha: [0.8, 0],
        fadeSize: [0.3, 0.05],
        blendingMode: 'additive',
        easeFunction: 'easeOutQuad',
        gravity: [0, -5, 0],
      },
      {
        loop: false,
        duration: 0.02,
        nbParticles: 15,
        spawnMode: 'burst',
        particlesLifetime: [0.1, 0.25],
        startPositionMin: [0, 0, 0],
        startPositionMax: [0, 0, 0],
        // Spread outward from hit normal
        directionMin: [-0.8, -0.5, -0.8],
        directionMax: [0.8, 1, 0.8],
        size: [0.05, 0.12],
        speed: [3, 8],
        colorStart: ['#ffdd00', '#ffaa00'], // Bright yellow sparks
        colorEnd: ['#ff6600', '#ff0000'],   // Fade to orange/red
        useLocalDirection: false,
      },
      {
        position: hitInfo.hitLocation.clone(),
      }
    );
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_InstantHitWeapon';
  }
}
